import { ApolloError } from '@apollo/client'
import { yupResolver } from '@hookform/resolvers/yup'
import { Typography, Grid, FormControlLabel, Checkbox, Link, Button } from '@mui/material'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { Stack } from '@mui/system'
import { useForm } from 'react-hook-form'
import { LoginFormValues } from './types'
import { validationLogin } from './validation'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'components/global/CUK/Toast/Toast'
import { BYPASSES_DISCLAIMER_KEY } from 'variables'

import useActions from '../../../use-actions'
import LoginEmail from '../../components/LoginEmail'
import LoginPassword from '../../components/LoginPassword'
import ErrorMessage from '../../components/ErrorMessage'
import LoginButton from '../../components/LoginButton'
import LoginPaper from '../../components/LoginPaper'
import { getGraphQLErrorCode, ERROR_MESSAGES } from './errorHandler'

const LoginPanel = ({identityProviderRedirectUrl}:{identityProviderRedirectUrl?: string}) => {
  const { login } = useActions()
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [apolloErrors, setApolloErrors] = useState<ApolloError[] | null>(null)
  const navigate = useNavigate()
  const {
    handleSubmit,
    register,
    formState: { errors, isValid }
  } = useForm<LoginFormValues>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(validationLogin(t))
  })
  
  const handleLogin = async (data: LoginFormValues) => {
    const { email, password } = data
    try {
      setLoading(true)
      const { errors, data: {two_fa_activated, two_fa_disclaimer} = {} } = await login(email, password)
      if (errors) {
        const errorCode = getGraphQLErrorCode(errors as ApolloError) as string
        if (errorCode && ERROR_MESSAGES[errorCode]) {
          toast.error(t(ERROR_MESSAGES[errorCode]))
        } else {
          setApolloErrors(errors as ApolloError[])
        }
      } else if (two_fa_disclaimer) {
        navigate('/auth/two_fa_disclaimer')
      } else if (two_fa_activated) {
        navigate('/auth/two_fa_code_validation')
      } else if (identityProviderRedirectUrl) {
        window.location.href = identityProviderRedirectUrl
      } else {
        localStorage.removeItem(BYPASSES_DISCLAIMER_KEY)
        window.location.href = '/auth/me'
      }
    } catch (err: unknown) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && isValid && !loading) {
        event.preventDefault()
        handleSubmit(handleLogin)()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isValid, loading, handleSubmit])

  return (
    <LoginPaper>
      <Stack gap={2} sx={{ alignItems: 'center', maxWidth: '400px' }}>
        <Typography
          sx={{
            fontSize: '36px',
            lineHeight: '54px',
            fontWeight: '600',
            color: 'content.secondary',
            '&::after': {
              bottom: 0,
              color: 'common.accent',
              content: '" !"',
              position: 'relative',
              transform: 'translate(-100%, 0)'
            }
          }}
        >
          {t('login.welcome')}
        </Typography>

        <ErrorMessage
          sx={{
            visibility: apolloErrors ? 'visible' : 'hidden'
          }}
        >
          {t('login.error.invalid_login')}
        </ErrorMessage>

        <Grid container rowSpacing={2} maxWidth="inherit">
          <Grid item xs={12}>
            <Stack gap={2}>
              <LoginEmail
                error={Boolean(errors.email)}
                errorMessage={errors.email?.message}
                InputProps={{
                  ...register('email', { required: true })
                }}
              />
              <LoginPassword
                placeholder={t('login.connection.password')}
                error={Boolean(errors.password)}
                errorMessage={errors.password?.message}
                InputProps={{
                  ...register('password', { required: true })
                }}
              />
            </Stack>
          </Grid>
          <Grid item xs={6} sx={{ pl: '15px' }}>
            <FormControlLabel
              control={
                <Checkbox
                  defaultChecked
                  checkedIcon={<i className="fa-solid fa-square-check"></i>}
                  icon={<i className="fa-regular fa-square"></i>}
                  sx={{
                    p: 0,
                    pr: '10px'
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    fontSize: '12px',
                    lineHeight: '18px',
                    color: 'content.secondary'
                  }}
                >
                  {t('login.remember_me')}
                </Typography>
              }
            />
          </Grid>
          <Grid item xs={6} sx={{ pr: '20px' }} display={'flex'} alignItems={'center'} justifyContent={'flex-end'}>
            <Link
              sx={{
                fontSize: '12px',
                lineHeight: '18px',
                fontWeight: 400,
                color: 'common.accent',
                textDecoration: 'none'
              }}
              component={RouterLink}
              to="/auth/password/recovery"
            >
              {t('login.remember_password')}
            </Link>
          </Grid>
          <Grid item xs={12}>
            <LoginButton disabled={!isValid} loading={loading} onClick={handleSubmit(handleLogin)}>
              {t('login.connexion')}
            </LoginButton>
          </Grid>
          <Grid item xs={12}>
            <Stack gap={2} alignItems="center">
              <Typography
                sx={{
                  fontSize: '12px',
                  lineHeight: '18px',
                  fontWeight: '400',
                  color: 'content.tertiary'
                }}
              >
                {t('login.continue_to')}
              </Typography>
              <Button
                variant="outlined"
                sx={{
                  fontSize: '12px',
                  lineHeight: '18px',
                  fontWeight: '500',
                  color: 'common.accent',
                  borderRadius: '30px',
                  width: 'fit-content',
                  padding: '3px 20px'
                }}
                endIcon={<i style={{ fontSize: 'inherit' }} className="fa-regular fa-arrow-up-right-from-square"></i>}
                onClick={() => {
                  window.location.href = 'directory_sso'
                }}
              >
                {t('login.wifirst_account')}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Stack>
    </LoginPaper>
  )
}

export default LoginPanel
